import { Table } from "react-bootstrap";
import { Kpi } from "./Kpi";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildStatement,
  monthlyToPeriods,
  subtotal,
} from "../../../lib/incomeStatementData";

const money = (value) => {
  const s = Math.abs(value).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `(${s})` : s;
};

const fmtDate = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime())
    ? String(d)
    : date.toLocaleDateString("en-KE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

/**
 * @typedef {Object} FinancialReportProps
 * @property {string} startDate
 * @property {string} endDate
 * @property {boolean} monthly
 * @property {[]} incomeStData
 * @property {{}} monthlyIncomeStData
 * /

/**
 * @param {FinancialReportProps} props
 */

export function FinancialReport({
  startDate,
  endDate,
  monthly = true,
  incomeStData,
  monthlyIncomeStData,
} = {}) {
  const monthlyPeriods = monthlyToPeriods(monthlyIncomeStData || []);
  const rangeLabel =
    startDate || endDate
      ? `${fmtDate(startDate)} - ${fmtDate(endDate)}`
      : monthlyPeriods.length
        ? `${monthlyPeriods[0].label} - ${monthlyPeriods[monthlyPeriods.length - 1].label}`
        : "Selected period";

  const totals = buildStatement([{ label: "Total", rows: incomeStData || [] }]);
  const monthStatement = buildStatement(monthlyPeriods);

  let table;
  if (monthly) {
    const merged = buildStatement([
      ...monthlyPeriods,
      { label: "Total", rows: incomeStData || [] },
    ]);
    table = { ...merged, totalCol: merged.columns.length - 1 };
  } else table = { ...totals, totalCol: 0 };

  const kpi = (f) => subtotal(totals, f, 0);
  const chartSource =
    monthly && monthlyPeriods.length ? monthStatement : totals;
  const chartData = chartSource.columns.map((label, c) => ({
    period: label,
    revenue: subtotal(chartSource, "total income", c),
    expenses: -subtotal(chartSource, "total expenses", c),
    netProfit: subtotal(chartSource, "surplus", c),
  }));

  function exportCsv() {
    const rows = [["Income Statement", ...table.columns]];
    table.lines.forEach((l) =>
      rows.push([
        l.kind === "account" ? `  ${l.label}` : l.label,
        ...(l.kind === "group"
          ? table.columns.map(() => "")
          : l.values.map((v) => v.toFixed(2))),
      ]),
    );
    const blob = new Blob(
      [
        rows
          .map((r) =>
            r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(","),
          )
          .join("\r\n"),
      ],
      { type: "text/csv;charset=utf-8;" },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `income-statement-${rangeLabel.replace(/[^\w]+/g, "-")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      {/* <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4">
        <div className="small">
          <span className="fw-semibold">Period:</span> {rangeLabel} ·{" "}
          {monthly ? "Month-by-month comparison" : "Cumulative"}
        </div>
        <Button size="sm" variant="outline-secondary" onClick={exportCsv}>
          <i className="bi bi-download me-2" />
          Export CSV
        </Button>
      </div> */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-xl-3">
          <Kpi
            label="Total income"
            value={`KSh ${money(kpi("total income"))}`}
            icon="bi-cash-stack"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <Kpi
            label="Gross income"
            value={`KSh ${money(kpi("gross"))}`}
            icon="bi-graph-up"
            tone="success"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <Kpi
            label="Total expenses"
            value={`KSh ${money(kpi("total expenses"))}`}
            icon="bi-receipt"
            tone="warning"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <Kpi
            label="Surplus / (deficit)"
            value={`KSh ${money(kpi("surplus"))}`}
            icon="bi-bank"
            tone={kpi("surplus") >= 0 ? "success" : "danger"}
          />
        </div>
      </div>
      <section
        className="pbi-financial-chart mb-4"
        aria-label="Income statement chart"
      >
        <div className="mb-3">
          <h2 className="h5 fw-bold mb-1">Income, expenses & surplus</h2>
          <div className="small text-muted">
            {rangeLabel} · KSh ·{" "}
            {monthly ? "Monthly performance" : "Period totals"}
          </div>
        </div>
        {monthly && (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart
              data={chartData}
              margin={{ top: 16, right: 20, left: 8, bottom: 8 }}
              accessibilityLayer
            >
              <CartesianGrid
                vertical={false}
                stroke="var(--financial-rule)"
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 12 }}
                stroke="var(--financial-muted)"
                tickLine={false}
                axisLine={false}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={(v) =>
                  Math.abs(v) >= 1000000
                    ? `${(v / 1000000).toFixed(1)}m`
                    : `${Math.round(v / 1000)}k`
                }
                tick={{ fontSize: 12 }}
                stroke="var(--financial-muted)"
                tickLine={false}
                axisLine={false}
                width={64}
              />
              <Tooltip
                formatter={(value, name) => [
                  `KSh ${money(Number(value))}`,
                  name,
                ]}
                contentStyle={{
                  background: "var(--financial-surface)",
                  borderColor: "var(--financial-rule)",
                  borderRadius: 6,
                }}
                wrapperStyle={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  zIndex: 10,
                }}
              />
              <Legend
                wrapperStyle={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  zIndex: 10,
                }}
              />
              <Bar
                dataKey="revenue"
                name="Total income"
                fill="var(--financial-revenue)"
                maxBarSize={48}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              />
              <Bar
                dataKey="expenses"
                name="Total expenses"
                fill="var(--financial-expenses)"
                maxBarSize={48}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              />
              <Line
                dataKey="netProfit"
                name="Surplus / (deficit)"
                stroke="var(--financial-profit)"
                strokeWidth={3}
                dot={{ r: 4 }}
                type="monotone"
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </section>
      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h2 className="h5 fw-bold mb-1">Income Statement</h2>
        </div>
        <div className="small text-muted text-end">
          {monthly
            ? "Month-by-month comparison"
            : "Cumulative for selected period"}
        </div>
      </div>
      <Table responsive className="pbi-table pbi-income-table mb-0">
        <thead>
          <tr>
            <th scope="col">Account</th>
            {table.columns.map((c, i) => (
              <th
                key={c}
                scope="col"
                className={`text-end text-nowrap ${i === table.totalCol ? "fw-bold" : ""}`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.lines.map((l, idx) => {
            if (l.kind === "group")
              return (
                <tr key={idx} className="pbi-income-section">
                  <th colSpan={table.columns.length + 1} scope="rowgroup">
                    {l.label}
                  </th>
                </tr>
              );
            const isNet = l.label.toLowerCase().includes("surplus");
            return (
              <tr
                key={idx}
                className={
                  l.kind === "subtotal"
                    ? `pbi-income-total ${isNet ? "pbi-income-net" : ""}`
                    : ""
                }
              >
                <th
                  scope="row"
                  className={`text-nowrap ${l.kind === "account" ? "fw-normal ps-4" : ""}`}
                >
                  {l.label}
                </th>
                {l.values.map((v, i) => (
                  <td
                    key={i}
                    className={`text-end text-nowrap ${i === table.totalCol && l.kind === "account" ? "fw-semibold" : ""}`}
                  >
                    {money(v)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
